<template>
    <div>
        <input
            type="hidden"
            v-if="reuseSmsValidationId != null"
            name="reuseSmsValidationId"
            :value="reuseSmsValidationId"
        />
        <transition
            :enter-active-class="$style['enter']"
            :leave-active-class="$style['leave']"
            mode="out-in"
        >
            <div
                :class="['phone-validation', $style['view-3']]"
                v-if="finishedSmsValidation"
                key="finished-validation"
            >
                {{
                    labels.frontendProductpage.bidding.phoneValidation.validated
                }}
                <input
                    type="hidden"
                    name="validationPhone"
                    :value="validationPhone"
                />
                <input
                    type="hidden"
                    name="smsValidationId"
                    :value="smsValidationId"
                />
            </div>
            <div
                :class="['phone-validation', $style['view-2']]"
                v-else-if="showCodeInput"
                key="enter-code"
            >
                {{
                    labels.frontendProductpage.bidding.phoneValidation
                        .smsCodeHeader
                }}<br />
                <input
                    type="text"
                    v-model="validationCode"
                    :class="$style['code-input']"
                    :readonly="locked"
                />
                <cool-button inline size="small" style="">
                    <button
                        type="button"
                        @click="validateCode()"
                        :disabled="locked || !canClickValidate"
                    >
                        {{
                            labels.frontendProductpage.bidding.phoneValidation
                                .validateCodeBtn
                        }}
                    </button>
                </cool-button>
            </div>
            <div
                :class="['phone-validation', $style['view-1']]"
                v-else
                :style="{ display: isAlreadyValid ? 'none' : '' }"
                key="phone-validation"
            >
                {{ labels.frontendProductpage.bidding.phoneValidation.header
                }}<br />
                <input
                    type="text"
                    name="validationPhone"
                    :class="$style['phone-input']"
                    :readonly="locked"
                    v-model="validationPhone"
                    ref="field"
                    @focus="onFieldFocus($event)"
                    @blur="onFieldBlur()"
                />
                <template v-if="smsValidation">
                    <cool-button inline size="small" style="">
                        <button
                            type="button"
                            @click="sendCode()"
                            :disabled="locked || !canClickSend"
                        >
                            {{
                                labels.frontendProductpage.bidding
                                    .phoneValidation.sendCodeBtn
                            }}
                        </button>
                    </cool-button>
                    <input
                        type="hidden"
                        name="smsValidationId"
                        :value="smsValidationId"
                        v-if="smsValidation"
                    />
                </template>

                <span
                    :class="
                        $style['tooltip-action'] +
                        ' tooltip-hint popup-text gray small'
                    "
                    v-if="hasPopup"
                    ref="popupContainer"
                >
                    <span class="normal-size" style="white-space: pre-line">{{
                        labels.frontendProductpage.bidding.phoneValidation
                            .popupText
                    }}</span>
                </span>
            </div>
        </transition>
    </div>
</template>

<style module>
.phone-input {
    font-size: 14px;
    width: 135px;
    vertical-align: top;
    height: 23px;
    padding: 1px 5px;
    margin-right: 5px;
    border: 2px solid #272727;
    border-radius: 4px;
}

.tooltip-action {
    margin-left: 5px;
}

.code-input {
    font-size: 14px;
    width: 135px;
    vertical-align: top;
    height: 23px;
    padding: 1px 5px;
}

.enter {
    animation-name: animation-in;
    animation-duration: 0.4s;
    animation-timing-function: cubic-bezier(0, 0, 0, 1);
    animation-iteration-count: 1;
    animation-fill-mode: none;
}
.leave {
    animation-name: animation-out;
    animation-duration: 0.4s;
    animation-timing-function: cubic-bezier(0, 0, 0, 1);
    animation-iteration-count: 1;
    animation-fill-mode: none;
    animation-timing-function: ease-out;
}

.view-1,
.view-2,
.view-3,
.view-4 {
}

@keyframes animation-in {
    0% {
        opacity: 0;
        transform: translateX(-30px);
    }

    100% {
        opacity: 1;
        transform: translateX(0px);
    }
}

@keyframes animation-out {
    0% {
        opacity: 1;
        transform: translateX(0px);
    }

    100% {
        opacity: 0;
        transform: translateX(30px);
    }
}
</style>

<script setup lang="ts">
import axios from "axios";
import CoolButton from "../../../shared/CoolButton.vue";
import labels from "../../../shared/labels";
import {
    isNullOrEmpty,
    isNullOrWhitespace,
} from "../../../../../shared/utils/string";
import { computed, nextTick, ref, watch } from "vue";

const props = defineProps({
    productId: Number,
    smsValidation: Boolean,
    smsAlreadyValid: Boolean,
    existingValidationPhone: String,
    existingValidationId: Number,
    reuseSmsValidationId: Number,
});

const focusConfirmedRead = ref(false);
const hasPopup = ref(
    !isNullOrEmpty(labels.frontendProductpage.bidding.phoneValidation.popupText)
);
const locked = ref(false);
const showCodeInput = ref(false);
const finishedSmsValidation = ref(false);
const validationCode = ref("");
const validationPhone = ref(props.existingValidationPhone);
const smsValidationId = ref(props.existingValidationId);

const emit = defineEmits(["update:smsValidationId", "update:validationPhone"]);

watch(
    () => smsValidationId.value,
    (newValue) => {
        emit("update:smsValidationId", newValue);
    },
    {
        immediate: true,
    }
);

watch(
    () => validationPhone.value,
    (newValue) => {
        emit("update:validationPhone", newValue);
    },
    {
        immediate: true,
    }
);

const isAlreadyValid = computed(() => {
    if (props.smsValidation) {
        if (smsValidationId.value != null)
            if (props.smsAlreadyValid === true) return true;
    } else {
        if (
            typeof props.existingValidationPhone == "string" &&
            props.existingValidationPhone.length > 0
        ) {
            if (validationPhone.value == props.existingValidationPhone)
                return true;
        }
    }

    return false;
});
const canClickValidate = computed(() => {
    return !isNullOrWhitespace(validationCode.value);
});
const canClickSend = computed(() => {
    return !isNullOrWhitespace(validationPhone.value);
});

async function validateCode() {
    if (isAlreadyValid.value) throw new Error("Already valid");
    if (!props.smsValidation) throw new Error("Not sms validation");
    if (finishedSmsValidation.value) throw new Error("Already finished");
    if (!showCodeInput.value) throw new Error("Not show code input");
    if (locked.value) throw new Error("Locked");
    if (smsValidationId.value == null) throw new Error("No validation id");
    if (isNullOrWhitespace(validationCode.value)) throw new Error("No code");

    locked.value = true;
    try {
        const response = await axios.get("/auctionProduct/validate-code", {
            params: {
                validationId: smsValidationId.value,
                code: validationCode.value,
            },
        });
        const result = response.data;
        if (result.success != true) {
            alert(result.message);
            return;
        }
        finishedSmsValidation.value = true;

        showCodeInput.value = true;
    } finally {
        locked.value = false;
    }
}
async function sendCode() {
    if (isAlreadyValid.value) throw new Error("Already valid");
    if (!props.smsValidation) throw new Error("Not sms validation");
    if (locked.value) throw new Error("Locked");

    locked.value = true;
    try {
        const response = await axios.get("/auctionProduct/send-code", {
            params: {
                productId: props.productId,
                phoneNumber: validationPhone.value,
            },
        });
        const result = response.data;
        if (result.success != true) {
            alert(result.message);
            return;
        }
        if (result.alreadyValid) {
            smsValidationId.value = result.validationId;
            finishedSmsValidation.value = true;
            return;
        }

        if (!result.sent) {
            alert("Error. Sms not sent.");
            return;
        }

        smsValidationId.value = result.validationId;
        showCodeInput.value = true;
    } finally {
        locked.value = false;
    }
}

const popupContainer = ref<HTMLSpanElement>();
const field = ref<HTMLInputElement>();

// Safari-specific fix: Prevent endless loop of blur and focus.
const dialogOpen = ref(false);

function onFieldFocus(e: Event) {
    if (dialogOpen.value) {
        // console.debug("focus field (cancelled)");
        return;
    }
    // console.debug("focus field");
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    if (!focusConfirmedRead.value) {
        if (hasPopup.value && popupContainer.value != null) {
            dialogOpen.value = true;
            const popup = window.$(popupContainer.value);
            popup.data("customization", function (configuration: any) {
                configuration.onHide = function (hash: any) {
                    // console.debug("on hide");
                    dialogOpen.value = false;
                    return window.$.jqm.params.onHide(hash);
                };
                return configuration;
            });
            popup.data("onceOk", [
                function () {
                    // console.debug("onceOk");
                    focusConfirmedRead.value = true;
                    nextTick(function () {
                        field.value?.focus();
                    });
                },
            ]);
            popup.click();

            return;
        }
    }
}
function onFieldBlur() {
    if (dialogOpen.value) {
        // console.debug("blur field (cancelled)");
        return;
    }
    // console.debug("blur field");
    focusConfirmedRead.value = false;
}
</script>
